import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getPendingRecordsByProject,
  type PendingRecord,
  type PendingRecordStatus,
} from "@/offline/db";
import type { ProjectImage, ProjectRecord } from "@/types/api";

/**
 * Query key for the local outbox of a project. Kept as a free-standing
 * helper so the drain/submit/retry paths invalidate the exact same literal
 * key the grid reads from — `["pendingRecords", projectId]`.
 *
 * NOTE: this MUST stay the literal `["pendingRecords", projectId]`. Sibling
 * outbox tasks (submit, drain, tap-to-retry) invalidate this same key by
 * the literal; do not route it through a `query-keys.ts` factory.
 */
export function pendingRecordsKey(projectId: string) {
  return ["pendingRecords", projectId] as const;
}

/**
 * A pending/failed outbox record decorated with a ready-to-render object URL
 * for its locally captured thumbnail. The grid renders these tiles ahead of
 * the server records; the `<img>` points straight at `thumbnailUrl` — no
 * network, no offline-image-cache lookup — because the blob is already in
 * hand.
 *
 * The tile also carries the draft's editable metadata (plan, pin, tags,
 * dynamic fields). The grid uses these to synthesize a `ProjectRecord` rich
 * enough that [[image-viewer-modal.spec]] opens with a non-empty image AND a
 * pre-populated metadata form — a pending record is a fully viewable,
 * editable local draft. See [[offline-record-outbox]].
 */
export interface PendingRecordTile {
  clientUuid: string;
  projectId: string;
  status: PendingRecordStatus;
  /** Object URL minted from the locally captured annotated blob. */
  thumbnailUrl: string;
  /** Bumped on each local edit — drives object-URL re-minting. */
  revision: number;
  createdAt: number;
  /** Draft metadata — mirrored so the modal's edit form pre-populates. */
  planId: string | null;
  pinX: number | null;
  pinY: number | null;
  /** Plan-relative bearing in degrees `[0, 360)`, or null/undefined for none. */
  pinBearing?: number | null;
  tags: string[];
  metadata: Record<string, string | number | boolean | string[]>;
  /**
   * Total photos in the record: `1` for a single-photo entry (no `photos`),
   * `photos.length` for a multi-photo entry. Drives the photo-count badge a
   * multi-photo tile shows — identical to a synced multi record's badge. See
   * [[photo-grid-item.spec]] US-004 and [[multi-photo-records]].
   */
  photoCount: number;
  /**
   * How many of this entry's photos have landed on the server
   * (`uploadStatus === "synced"`): `0` for a single-photo entry (it is not
   * surfaced — a single tile is whole-or-nothing), the count of synced photos
   * for a multi-photo entry. Reserved for a future "3 of 5 uploaded" progress
   * affordance; the count badge itself shows `photoCount`.
   */
  syncedCount: number;
}

/**
 * Synthesize the `ProjectRecord` shape used to render an outbox record that
 * has not yet synced — as a cell in [[photo-grid.spec]], as a pin on
 * [[plan-viewer.spec]], and, on tap, in [[image-viewer-modal.spec]].
 *
 * `image.image` is the outbox annotated-blob object URL, NOT an empty string:
 * `records-view` passes `selectedRecord.image.image` straight through as the
 * modal's `imageSrc`, and the modal bails on a falsy `imageSrc`. An empty
 * string is non-nullish, so it would slip past the `?? null` fallback and
 * leave the modal rendering nothing — a tapped pending tile would look dead.
 * Pointing `image.image` at the in-hand blob URL makes a pending record a
 * fully viewable local draft, online or offline, with no server call.
 *
 * Metadata (plan, pin, tags, dynamic fields) is copied from the outbox draft
 * so the modal's edit form pre-populates correctly. Markup re-edit reads the
 * original bytes + annotations straight from the outbox entry inside the
 * modal, so they are not threaded here.
 *
 * The `id` is the stable `clientUuid`; the modal matches it against the
 * `["pendingRecords"]` query to classify the record as a pending draft.
 *
 * Multi-photo (`photoCount > 1`): a synthesized `images[]` of length
 * `photoCount` is attached so the grid's count badge — which reads
 * `recordImages(record).length` via [[record-images]] — lights up for a
 * pending multi tile exactly as it does for a synced multi record. Only the
 * COVER (`images[0]`) carries a real URL; the rest reuse the cover URL purely
 * as placeholders, because nothing renders them: the grid tile draws
 * `tile.thumbnailUrl` directly (the `outbox` branch in
 * [[photo-grid-item.spec]]), and the modal builds the real per-photo grid from
 * the outbox entry's `photos[]` on open (it reads `images[]` only for a synced
 * server record). Single-photo tiles keep the legacy single `image` shape
 * unchanged.
 */
export function outboxPlaceholderRecord(tile: PendingRecordTile): ProjectRecord {
  const cover: ProjectImage = {
    id: tile.clientUuid,
    image: tile.thumbnailUrl,
    thumbnail: null,
    taken_at: null,
  };
  return {
    id: tile.clientUuid,
    // A single-photo tile keeps the legacy top-level `image`; a multi-photo
    // tile carries `images[]` (length === photoCount) so the count badge
    // renders. The two shapes mirror the v1.x vs v2.0 reads `recordImages`
    // already normalizes over, so every downstream reader stays correct.
    ...(tile.photoCount > 1
      ? {
          images: Array.from({ length: tile.photoCount }, (_, i) =>
            i === 0
              ? cover
              : // Placeholder for the count only — never rendered. A distinct
                // `id` keeps React keys unique if a reader ever maps over them.
                { ...cover, id: `${tile.clientUuid}:${i}` },
          ),
        }
      : { image: cover }),
    plan: tile.planId,
    pin_x: tile.pinX,
    pin_y: tile.pinY,
    pin_bearing: tile.pinBearing ?? null,
    tags: tile.tags,
    metadata: tile.metadata,
    created_by: {
      id: "",
      email: "",
      first_name: "",
      last_name: "",
    },
    created_at: new Date(tile.createdAt).toISOString(),
    updated_at: new Date(tile.createdAt).toISOString(),
  };
}

/** Identity for the object-URL cache: a blob is fresh per uuid + revision. */
function tileCacheKey(record: PendingRecord): string {
  return `${record.clientUuid}:${record.revision}`;
}

/**
 * Reads the project's local outbox (pending + failed records) as a TanStack
 * query keyed `["pendingRecords", projectId]`, and hands back tiles whose
 * thumbnails are object URLs minted from the locally captured annotated
 * blob.
 *
 * Entries flagged `deleted` are filtered OUT — a pending record the user
 * deleted must disappear from the grid the instant the delete lands, even
 * while its create upload is still in flight (the drain still sees the
 * `deleted` entry via `getAllPendingRecords` and fires the compensating
 * server `DELETE`; only the *display* hides it). See [[offline-record-outbox]].
 *
 * Object-URL lifecycle: a URL is created the first time a given
 * `clientUuid:revision` is seen and reused on subsequent reads; it is
 * revoked when that record leaves the outbox (synced/deleted), when its
 * `revision` advances (a new blob), and when the hook unmounts. No leaks.
 *
 * The query is invalidated by submit, the drain, and tap-to-retry via the
 * literal `["pendingRecords", projectId]` key.
 */
export function usePendingRecords(projectId: string): PendingRecordTile[] {
  const query = useQuery({
    queryKey: pendingRecordsKey(projectId),
    queryFn: () => getPendingRecordsByProject(projectId),
    // The outbox is local IndexedDB; only mutations (submit/drain/retry)
    // change it and they invalidate this key explicitly. No background
    // refetch churn needed.
    staleTime: Infinity,
    gcTime: 0,
  });

  // A `deleted` entry is a pending record the user removed — it is gone
  // from the user's point of view immediately, so it never renders a tile.
  // The drain still reconciles it; the display simply hides it.
  const records = useMemo(
    () => query.data?.filter((record) => !record.deleted),
    [query.data],
  );

  // Object-URL cache keyed by `clientUuid:revision`, held in STATE (not a
  // ref). All minting and revoking happens in the effect below — never
  // during render — so render is a pure read of this map. A URL is created
  // once per `clientUuid:revision` and reused; it is revoked when its record
  // leaves the outbox or its `revision` advances (a fresh blob).
  const [urlCache, setUrlCache] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );

  // Reconcile the URL cache against the current records: mint a URL for any
  // newly-seen `clientUuid:revision`, revoke any that has gone stale. This
  // is the ONLY place object URLs are created or revoked — every URL side
  // effect is kept out of render (the `react-hooks/refs` rule forbids
  // touching the cache during render). Synchronising an external resource
  // (object URLs) with an async-query result genuinely needs a `setState`
  // here; the updater is a no-op (`return prev`) whenever nothing changed,
  // so it cannot loop — hence the scoped `set-state-in-effect` disable.
  useEffect(() => {
    if (!records) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- object-URL lifecycle sync with async query data; guarded no-op when unchanged
    setUrlCache((prev) => {
      const liveKeys = new Set(records.map(tileCacheKey));
      let changed = false;
      const next = new Map(prev);
      // Revoke + drop any URL whose record left the outbox / revision moved.
      for (const [key, url] of prev) {
        if (!liveKeys.has(key)) {
          URL.revokeObjectURL(url);
          next.delete(key);
          changed = true;
        }
      }
      // Mint a URL for any record not yet in the cache.
      for (const record of records) {
        const key = tileCacheKey(record);
        if (!next.has(key)) {
          next.set(key, URL.createObjectURL(record.annotatedBlob));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [records]);

  // Mirror the latest URL cache into a ref so the unmount cleanup below can
  // revoke every URL WITHOUT calling a state setter on an unmounting hook.
  // A ref read inside an effect / cleanup (not render) is fine.
  const urlCacheRef = useRef(urlCache);
  useEffect(() => {
    urlCacheRef.current = urlCache;
  }, [urlCache]);

  // Revoke every outstanding URL when the hook unmounts — no leaks.
  useEffect(() => {
    return () => {
      for (const url of urlCacheRef.current.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  // Pure derivation: build the tiles from `records` and the state-backed URL
  // cache. A record whose URL has not been minted yet (the effect runs after
  // this render) is skipped — it appears on the very next render once the
  // cache state updates. No ref access, no side effects.
  return useMemo<PendingRecordTile[]>(() => {
    if (!records) return [];
    // Newest-first so a freshly captured record is the first tile.
    const sorted = [...records].sort((a, b) => b.createdAt - a.createdAt);
    const tiles: PendingRecordTile[] = [];
    for (const record of sorted) {
      const url = urlCache.get(tileCacheKey(record));
      if (!url) continue;
      // Photo counts: a multi entry carries `photos[]`; a single entry (no
      // `photos`) is one photo whose `syncedCount` is not surfaced (single
      // tiles are whole-or-nothing, so it stays 0 while pending). `?? []`
      // gives a single entry length 0 for the filter, then `|| 1` floors the
      // count at the one cover photo it always has.
      const photos = record.photos ?? [];
      const photoCount = photos.length || 1;
      const syncedCount = photos.filter(
        (photo) => photo.uploadStatus === "synced",
      ).length;
      tiles.push({
        clientUuid: record.clientUuid,
        projectId: record.projectId,
        status: record.status,
        thumbnailUrl: url,
        revision: record.revision,
        createdAt: record.createdAt,
        planId: record.planId,
        pinX: record.pinX,
        pinY: record.pinY,
        pinBearing: record.pinBearing ?? null,
        tags: record.tags,
        metadata: record.metadata,
        photoCount,
        syncedCount,
      });
    }
    return tiles;
  }, [records, urlCache]);
}

/**
 * Lightweight companion to {@link usePendingRecords}: returns just the count
 * of live (non-`deleted`) outbox entries for a project — no object-URL
 * minting, no blob work.
 *
 * It reads the SAME `["pendingRecords", projectId]` query, with the same
 * `staleTime`/`gcTime`, so mounting this alongside the grid's
 * `usePendingRecords` is one shared IDB read, not two. `deleted` entries are
 * excluded for the same reason `usePendingRecords` hides them: a record the
 * user removed is gone from their point of view immediately.
 *
 * The records route uses this to keep the empty-state gate honest — a project
 * with zero server records but a non-empty outbox (queued offline uploads)
 * must render the grid, not the empty state. See [[records-page.spec]].
 */
export function usePendingRecordsCount(projectId: string): number {
  const { data: count = 0 } = useQuery({
    queryKey: pendingRecordsKey(projectId),
    queryFn: () => getPendingRecordsByProject(projectId),
    staleTime: Infinity,
    gcTime: 0,
    select: (records) => records.filter((record) => !record.deleted).length,
  });
  return count;
}
